// Tiny, dependency-free vanilla scripts for behaviour the STATIC published
// site needs.
//
// Why these exist at all: the published external site is prerendered with
// `renderToStaticMarkup` and never hydrates, so a React hook silently does
// nothing there. The codebase already established this pattern before this
// module — the hero carousel, the countdown ticker and the auto-hide header
// each inlined a hand-written script. Collecting them here means:
//
//   • every surface emits the SAME implementation (the interactive surfaces
//     run these too, rather than maintaining a parallel React version), and
//   • each one is independently testable as a pure string.
//
// House rules for anything added here:
//   1. Guard against double-execution (`if (window.__x) return`).
//   2. Never be required for the content to be READABLE — the page must be
//      complete before any of this runs.
//   3. Honour `prefers-reduced-motion` wherever motion is involved.
//   4. No dependencies, no build step, ES5-safe syntax.

import type { SiteAnalytics } from '@twasta/page-schema'
import { activityTrackerRuntime } from './activity/tracker'
import type { ActivityTrackerConfig } from './activity/tracker'

// ── Mobile navigation drawer ──────────────────────────────────────────
// The DRAWER ITSELF is pure CSS (a hidden checkbox toggles it), so it opens
// and closes on the static site with this script absent. What the script
// adds is the polish a checkbox can't express: Escape to close, closing
// after a link is followed, and locking body scroll while it is open.
export function navDrawerScript(): string {
  return `(function(){
  if(window.__pgNavDrawer)return;window.__pgNavDrawer=1;
  function boxes(){return document.querySelectorAll('.pg-nav-toggle');}
  function closeAll(){var b=boxes();for(var i=0;i<b.length;i++)b[i].checked=false;sync();}
  function sync(){
    var open=false,b=boxes();
    for(var i=0;i<b.length;i++)if(b[i].checked)open=true;
    document.body.style.overflow=open?'hidden':'';
  }
  document.addEventListener('change',function(e){
    var t=e.target;
    if(t&&t.classList&&t.classList.contains('pg-nav-toggle'))sync();
  });
  document.addEventListener('keydown',function(e){if(e.key==='Escape')closeAll();});
  document.addEventListener('click',function(e){
    var a=e.target&&e.target.closest?e.target.closest('.pg-nav-drawer a'):null;
    if(a)closeAll();
  });
  /* A resize past the breakpoint must not leave the page scroll-locked with
     an invisible drawer. */
  window.addEventListener('resize',function(){
    var b=boxes();
    for(var i=0;i<b.length;i++){
      var bp=parseInt(b[i].getAttribute('data-bp')||'768',10);
      if(window.innerWidth>bp)b[i].checked=false;
    }
    sync();
  },{passive:true});
})();`
}

// ── Scroll-spy + active link ──────────────────────────────────────────
// Marks the nav link whose target section is currently on screen. Works for
// both `#anchor` one-page navs and multi-page sites (where the active link is
// simply the one matching the current path, done in CSS-free JS so the
// static export doesn't need per-page markup).
export function scrollSpyScript(): string {
  return `(function(){
  if(window.__pgSpy)return;window.__pgSpy=1;
  var links=[].slice.call(document.querySelectorAll('.pg-navmenu a[href]'));
  if(!links.length)return;

  /* 1. Current-page highlight — compare normalised pathnames. */
  var here=location.pathname.replace(/\\/+$/,'')||'/';
  links.forEach(function(a){
    var href=a.getAttribute('href')||'';
    if(href.charAt(0)==='#'||/^[a-z]+:/i.test(href))return;
    var path=a.pathname?a.pathname.replace(/\\/+$/,'')||'/':href;
    if(path===here)a.setAttribute('data-pg-active','');
  });

  /* 2. Scroll-spy for in-page anchors. */
  var anchors=links.filter(function(a){return (a.getAttribute('href')||'').charAt(0)==='#';});
  if(!anchors.length||!('IntersectionObserver' in window))return;
  var byId={};
  var targets=[];
  anchors.forEach(function(a){
    var id=(a.getAttribute('href')||'').slice(1);
    if(!id)return;
    var el=document.getElementById(id);
    if(!el)return;
    byId[id]=a;targets.push(el);
  });
  if(!targets.length)return;
  var visible={};
  var io=new IntersectionObserver(function(entries){
    entries.forEach(function(e){
      if(e.isIntersecting)visible[e.target.id]=e.intersectionRatio;
      else delete visible[e.target.id];
    });
    /* The "current" section is the most-visible one, so a short section
       sandwiched between tall ones still wins while it dominates the view. */
    var best=null,bestRatio=0;
    for(var id in visible){if(visible[id]>bestRatio){bestRatio=visible[id];best=id;}}
    anchors.forEach(function(a){a.removeAttribute('data-pg-current');});
    if(best&&byId[best])byId[best].setAttribute('data-pg-current','');
  },{rootMargin:'-20% 0px -55% 0px',threshold:[0,.25,.5,.75,1]});
  targets.forEach(function(t){io.observe(t);});
})();`
}

// ── Marquee seamless loop ─────────────────────────────────────────────
// The CSS translates the track by exactly -50%, which only looks seamless if
// the track holds TWO copies of the items. Duplicating in markup would double
// the content for screen readers and crawlers, so the clone is made here and
// marked `aria-hidden` + `data-pg-mq-clone` (the reduced-motion CSS hides it).
export function marqueeDuplicate(): string {
  return `(function(){
  if(window.__pgMarquee)return;window.__pgMarquee=1;
  function build(){
    var tracks=document.querySelectorAll('.pg-marquee-track:not([data-pg-mq-done])');
    for(var i=0;i<tracks.length;i++){
      var t=tracks[i];
      t.setAttribute('data-pg-mq-done','');
      var kids=[].slice.call(t.children);
      for(var k=0;k<kids.length;k++){
        var c=kids[k].cloneNode(true);
        c.setAttribute('aria-hidden','true');
        c.setAttribute('data-pg-mq-clone','');
        t.appendChild(c);
      }
    }
  }
  build();
  if(window.MutationObserver)new MutationObserver(build).observe(document.body,{childList:true,subtree:true});
})();`
}

// ── Count-up numbers ──────────────────────────────────────────────────
// Animates `[data-pg-count]` from 0 to its final value once it scrolls into
// view. The element's TEXT already holds the final value when this runs, so a
// reduced-motion visitor, an old browser, or a crawler all see the real
// figure — the animation only ever replaces text that was already correct.
export function countUpScript(): string {
  return `(function(){
  if(window.__pgCount)return;window.__pgCount=1;
  var reduce=window.matchMedia&&window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if(reduce||!('IntersectionObserver' in window))return;
  function fmt(n,dec,sep){
    var s=dec>0?n.toFixed(dec):String(Math.round(n));
    if(!sep)return s;
    var parts=s.split('.');
    parts[0]=parts[0].replace(/\\B(?=(\\d{3})+(?!\\d))/g,',');
    return parts.join('.');
  }
  function run(el){
    var target=parseFloat(el.getAttribute('data-pg-count'));
    if(isNaN(target))return;
    var dur=parseInt(el.getAttribute('data-pg-count-ms')||'1600',10);
    var dec=parseInt(el.getAttribute('data-pg-count-dec')||'0',10);
    var sep=el.getAttribute('data-pg-count-sep')!=='0';
    var start=null;
    function step(ts){
      if(start===null)start=ts;
      var p=Math.min(1,(ts-start)/dur);
      /* easeOutCubic — fast then settling, which reads as "counting up"
         rather than a linear ticker. */
      var e=1-Math.pow(1-p,3);
      el.textContent=fmt(target*e,dec,sep);
      if(p<1)requestAnimationFrame(step);
      else el.textContent=fmt(target,dec,sep);
    }
    requestAnimationFrame(step);
  }
  var io=new IntersectionObserver(function(es){
    es.forEach(function(e){
      if(!e.isIntersecting)return;
      io.unobserve(e.target);
      run(e.target);
    });
  },{threshold:.4});
  function scan(){
    var els=document.querySelectorAll('[data-pg-count]:not([data-pg-count-armed])');
    for(var i=0;i<els.length;i++){els[i].setAttribute('data-pg-count-armed','');io.observe(els[i]);}
  }
  scan();
  if(window.MutationObserver)new MutationObserver(scan).observe(document.body,{childList:true,subtree:true});
})();`
}

// ── Gallery lightbox ──────────────────────────────────────────────────
// Progressive enhancement over a plain link: each tile is authored as
// `<a href="<full image>">`, so with this script absent (or blocked) a click
// simply opens the image. The script intercepts that click and shows the
// image in an overlay with arrow-key / swipe paging instead.
export function lightboxScript(): string {
  return `(function(){
  if(window.__pgLightbox)return;window.__pgLightbox=1;
  var box=null,imgEl=null,capEl=null,group=[],idx=0;
  function build(){
    box=document.createElement('div');
    box.className='pg-lb';box.setAttribute('role','dialog');box.setAttribute('aria-modal','true');
    box.innerHTML='<button class="pg-lb-x" aria-label="Close">&times;</button>'+
      '<button class="pg-lb-p" aria-label="Previous">&#8249;</button>'+
      '<figure class="pg-lb-fig"><img alt=""><figcaption></figcaption></figure>'+
      '<button class="pg-lb-n" aria-label="Next">&#8250;</button>';
    document.body.appendChild(box);
    imgEl=box.querySelector('img');capEl=box.querySelector('figcaption');
    box.querySelector('.pg-lb-x').addEventListener('click',close);
    box.querySelector('.pg-lb-p').addEventListener('click',function(e){e.stopPropagation();go(-1);});
    box.querySelector('.pg-lb-n').addEventListener('click',function(e){e.stopPropagation();go(1);});
    box.addEventListener('click',function(e){if(e.target===box)close();});
  }
  function show(){
    var a=group[idx];
    imgEl.src=a.getAttribute('href');
    var cap=a.getAttribute('data-pg-cap')||'';
    capEl.textContent=cap;
    capEl.style.display=cap?'':'none';
    imgEl.alt=cap;
    var many=group.length>1;
    box.querySelector('.pg-lb-p').style.display=many?'':'none';
    box.querySelector('.pg-lb-n').style.display=many?'':'none';
  }
  function go(d){idx=(idx+d+group.length)%group.length;show();}
  function close(){
    if(!box)return;
    box.removeAttribute('data-open');
    document.body.style.overflow='';
  }
  function open(a){
    if(!box)build();
    var g=a.getAttribute('data-pg-lb');
    group=[].slice.call(document.querySelectorAll('[data-pg-lb="'+g+'"]'));
    idx=group.indexOf(a);if(idx<0)idx=0;
    show();
    box.setAttribute('data-open','');
    document.body.style.overflow='hidden';
  }
  document.addEventListener('click',function(e){
    var a=e.target&&e.target.closest?e.target.closest('[data-pg-lb]'):null;
    if(!a)return;
    e.preventDefault();
    open(a);
  });
  document.addEventListener('keydown',function(e){
    if(!box||!box.hasAttribute('data-open'))return;
    if(e.key==='Escape')close();
    else if(e.key==='ArrowLeft')go(-1);
    else if(e.key==='ArrowRight')go(1);
  });
})();`
}

// ── Before/after comparison ───────────────────────────────────────────
// The wipe is driven by a real `<input type=range>`, so the control works
// (keyboard included) with no JS at all — the static site gets a usable
// slider. This adds pointer dragging anywhere on the image.
export function beforeAfterScript(): string {
  return `(function(){
  if(window.__pgBA)return;window.__pgBA=1;
  function set(root,v){
    root.style.setProperty('--pg-ba',v+'%');
    var r=root.querySelector('input[type=range]');
    if(r&&r.value!=String(v))r.value=v;
  }
  document.addEventListener('input',function(e){
    var r=e.target;
    if(!r||!r.classList||!r.classList.contains('pg-ba-range'))return;
    var root=r.closest('.pg-ba');
    if(root)set(root,r.value);
  });
  function drag(e){
    var root=e.target&&e.target.closest?e.target.closest('.pg-ba'):null;
    if(!root||e.target.classList.contains('pg-ba-range'))return;
    var move=function(ev){
      var rect=root.getBoundingClientRect();
      var pt=ev.touches?ev.touches[0]:ev;
      var vertical=root.getAttribute('data-orient')==='vertical';
      var pct=vertical
        ?((pt.clientY-rect.top)/rect.height)*100
        :((pt.clientX-rect.left)/rect.width)*100;
      set(root,Math.max(0,Math.min(100,Math.round(pct))));
    };
    move(e);
    var up=function(){
      document.removeEventListener('mousemove',move);
      document.removeEventListener('touchmove',move);
      document.removeEventListener('mouseup',up);
      document.removeEventListener('touchend',up);
    };
    document.addEventListener('mousemove',move);
    document.addEventListener('touchmove',move,{passive:true});
    document.addEventListener('mouseup',up);
    document.addEventListener('touchend',up);
  }
  document.addEventListener('mousedown',drag);
  document.addEventListener('touchstart',drag,{passive:true});
})();`
}

// ── Cookie consent ────────────────────────────────────────────────────
// Stores the visitor's decision and, crucially, is what releases any
// analytics script that declared `requiresConsent`. The banner markup is
// server-rendered hidden; this only reveals it when no decision is stored,
// so a returning visitor never sees a flash of the banner.
export function consentScript(storageKey = 'pg_consent'): string {
  return `(function(){
  if(window.__pgConsent)return;window.__pgConsent=1;
  var KEY=${JSON.stringify(storageKey)};
  function read(){try{return JSON.parse(localStorage.getItem(KEY)||'null');}catch(e){return null;}}
  function write(v){try{localStorage.setItem(KEY,JSON.stringify(v));}catch(e){}}
  function release(state){
    /* Any script parked behind consent is emitted as
       <script type="text/plain" data-pg-consent="<category>">; swapping the
       type is what makes the browser finally execute it. */
    var parked=document.querySelectorAll('script[data-pg-consent]');
    for(var i=0;i<parked.length;i++){
      var p=parked[i],cat=p.getAttribute('data-pg-consent');
      if(!state||state[cat]!==true)continue;
      var s=document.createElement('script');
      for(var a=0;a<p.attributes.length;a++){
        var at=p.attributes[a];
        if(at.name==='type'||at.name==='data-pg-consent')continue;
        s.setAttribute(at.name,at.value);
      }
      s.text=p.text;
      p.parentNode.replaceChild(s,p);
    }
  }
  window.pgConsent={get:read,set:function(v){write(v);release(v);}};
  var banner=document.querySelector('.pg-consent');
  var stored=read();
  if(stored){release(stored);return;}
  if(!banner)return;
  banner.setAttribute('data-open','');
  function decide(all){
    var state={};
    var boxes=banner.querySelectorAll('input[data-pg-cat]');
    if(boxes.length){
      for(var i=0;i<boxes.length;i++){
        var c=boxes[i].getAttribute('data-pg-cat');
        state[c]=all?true:(boxes[i].required?true:!!boxes[i].checked);
      }
    }else{state.all=!!all;}
    if(all)state.all=true;
    window.pgConsent.set(state);
    banner.removeAttribute('data-open');
  }
  var acc=banner.querySelector('[data-pg-accept]');
  var dec=banner.querySelector('[data-pg-decline]');
  var sav=banner.querySelector('[data-pg-save]');
  var pref=banner.querySelector('[data-pg-prefs]');
  if(acc)acc.addEventListener('click',function(){decide(true);});
  if(dec)dec.addEventListener('click',function(){decide(false);});
  if(sav)sav.addEventListener('click',function(){decide(false);});
  if(pref)pref.addEventListener('click',function(){banner.setAttribute('data-prefs','');});
})();`
}

// ── Analytics ─────────────────────────────────────────────────────────
/**
 * The `<script>` markup for a site's analytics provider.
 *
 * When `requiresConsent` is set the tag is emitted as `type="text/plain"`
 * with a `data-pg-consent` marker, so the browser will NOT run it; the
 * consent script above swaps the type once the visitor opts in. That is the
 * difference between a compliant setup and one that fires on page load and
 * asks afterwards.
 */
export function analyticsSnippet(a: SiteAnalytics | undefined): string {
  if (!a || !a.provider) return ''
  const park = a.requiresConsent
  const typeAttr = park ? ` type="text/plain" data-pg-consent="${escAttr(a.requiresConsent!)}"` : ''
  const autoEvents = a.autoEvents !== false ? AUTO_EVENT_SCRIPT : ''

  switch (a.provider) {
    case 'ga4': {
      if (!a.id) return ''
      const id = escAttr(a.id)
      return `<script${typeAttr} async src="https://www.googletagmanager.com/gtag/js?id=${id}"></script>`
        + `<script${typeAttr}>window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}`
        + `gtag('js',new Date());gtag('config','${id}');${autoEvents}</script>`
    }
    case 'gtm': {
      if (!a.id) return ''
      const id = escAttr(a.id)
      return `<script${typeAttr}>(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});`
        + `var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;`
        + `j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})`
        + `(window,document,'script','dataLayer','${id}');${autoEvents}</script>`
    }
    case 'plausible': {
      if (!a.domain) return ''
      return `<script${typeAttr} defer data-domain="${escAttr(a.domain)}" src="https://plausible.io/js/script.js"></script>`
        + (autoEvents ? `<script${typeAttr}>${autoEvents}</script>` : '')
    }
    case 'umami': {
      if (!a.src || !a.id) return ''
      return `<script${typeAttr} async src="${escAttr(a.src)}" data-website-id="${escAttr(a.id)}"></script>`
        + (autoEvents ? `<script${typeAttr}>${autoEvents}</script>` : '')
    }
    case 'custom':
      return a.custom ? `<script${typeAttr}>${a.custom}</script>` : ''
    default:
      return ''
  }
}

// ── First-party activity tracker ───────────────────────────────────────
/**
 * The published static site's copy of the SAME tracker the React surfaces
 * run (./activity/tracker.ts) — embedded as the function's own source, so
 * there is one implementation. `headers` cannot cross into a static page
 * (functions do not serialise) and is dropped, so a static page's visitors
 * are known by their first-party visitor id; a signed-in visitor is named on
 * the interactive surfaces, which send their session bearer.
 */
export function activityTrackerScript(cfg: ActivityTrackerConfig): string {
  const { headers: _drop, ...plain } = cfg
  void _drop
  const json = JSON.stringify(plain).replace(/</g, '\\u003c')
  return `(${activityTrackerRuntime.toString()})(window, ${json});`.replace(/<\/script/gi, '<\\/script')
}

// Automatic interaction events, dispatched to whichever provider is present.
// Deliberately provider-agnostic: it calls `gtag`/`dataLayer`/`plausible`/
// `umami` only if that global exists, so one snippet serves them all.
const AUTO_EVENT_SCRIPT = `
(function(){
  function send(name,props){
    try{
      if(typeof gtag==='function')gtag('event',name,props||{});
      else if(window.dataLayer)window.dataLayer.push(Object.assign({event:name},props||{}));
      if(typeof plausible==='function')plausible(name,{props:props||{}});
      if(window.umami&&window.umami.track)window.umami.track(name,props||{});
    }catch(e){}
  }
  document.addEventListener('click',function(e){
    var el=e.target&&e.target.closest?e.target.closest('a,button'):null;
    if(!el)return;
    var label=(el.getAttribute('aria-label')||el.textContent||'').trim().slice(0,80);
    send('click',{label:label,href:el.getAttribute('href')||''});
  },true);
  document.addEventListener('submit',function(e){
    var f=e.target;
    if(!f||f.tagName!=='FORM')return;
    send('form_submit',{form:f.getAttribute('data-pg-form')||f.id||''});
  },true);
})();`

function escAttr(s: string): string {
  return String(s).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;')
}

// ── Pricing period toggle ─────────────────────────────────────────────
// Swaps every tier between its primary and alternate price. Both prices are
// present in the DOM (one hidden), so the page is complete and crawlable
// before this runs and the toggle is instant.
export function pricingToggleScript(): string {
  return `(function(){
  if(window.__pgPricing)return;window.__pgPricing=1;
  document.addEventListener('change',function(e){
    var t=e.target;
    if(!t||!t.classList||!t.classList.contains('pg-price-toggle'))return;
    var root=t.closest('.pg-pricing');
    if(!root)return;
    if(t.checked)root.setAttribute('data-alt','');else root.removeAttribute('data-alt');
  });
})();`
}

// ── Generic slide carousel ────────────────────────────────────────────
// Drives ANY `[data-pg-carousel]` root — the standalone carousel block and
// the testimonial slider both use it, matched by the same stable class names
// the hero carousel established (`is-active` on slides and dots).
export function carouselScript(): string {
  return `(function(){
  if(window.__pgCarousel)return;window.__pgCarousel=1;
  function init(root){
    if(root.hasAttribute('data-pg-car-done'))return;
    root.setAttribute('data-pg-car-done','');
    var slides=root.querySelectorAll('.pg-car-slide');
    var dots=root.querySelectorAll('.pg-car-dot');
    if(slides.length<2)return;
    var idx=0,iv=null;
    var ms=parseInt(root.getAttribute('data-autoplay')||'0',10);
    var reduce=window.matchMedia&&window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    function go(i){
      idx=((i%slides.length)+slides.length)%slides.length;
      for(var k=0;k<slides.length;k++)slides[k].classList.toggle('is-active',k===idx);
      for(var d=0;d<dots.length;d++)dots[d].classList.toggle('is-active',d===idx);
    }
    function start(){if(ms>0&&!reduce)iv=setInterval(function(){go(idx+1);},ms);}
    function restart(){if(iv)clearInterval(iv);start();}
    for(var d=0;d<dots.length;d++)(function(j){
      dots[j].addEventListener('click',function(e){e.preventDefault();go(j);restart();});
    })(d);
    var p=root.querySelector('[data-arrow="prev"]'),n=root.querySelector('[data-arrow="next"]');
    if(p)p.addEventListener('click',function(e){e.preventDefault();go(idx-1);restart();});
    if(n)n.addEventListener('click',function(e){e.preventDefault();go(idx+1);restart();});
    /* Pausing on hover keeps a reader from losing a quote mid-sentence. */
    root.addEventListener('mouseenter',function(){if(iv)clearInterval(iv);});
    root.addEventListener('mouseleave',restart);
    start();
  }
  function scan(){
    var roots=document.querySelectorAll('[data-pg-carousel]');
    for(var i=0;i<roots.length;i++)init(roots[i]);
  }
  scan();
  if(window.MutationObserver)new MutationObserver(scan).observe(document.body,{childList:true,subtree:true});
})();`
}

// ── Announcement bar dismissal ────────────────────────────────────────
export function announcementScript(): string {
  return `(function(){
  if(window.__pgAnnounce)return;window.__pgAnnounce=1;
  var bar=document.querySelector('.pg-announce');
  if(!bar)return;
  var key='pg_announce_'+(bar.getAttribute('data-id')||'default');
  try{if(localStorage.getItem(key)==='1'){bar.remove();return;}}catch(e){}
  bar.removeAttribute('hidden');
  var x=bar.querySelector('[data-pg-dismiss]');
  if(x)x.addEventListener('click',function(){
    try{localStorage.setItem(key,'1');}catch(e){}
    bar.remove();
  });
})();`
}

// ── In-page anchor scrolling ──────────────────────────────────────────
// Handles `scroll_to` intents and plain `#hash` links with a sticky-header
// offset. Without the offset the target lands UNDER a pinned header, which
// is the single most common complaint about one-page navigation.
export function anchorScrollScript(): string {
  return `(function(){
  if(window.__pgAnchor)return;window.__pgAnchor=1;
  function headerOffset(){
    var h=document.querySelector('.pg-sticky-header');
    return h?h.offsetHeight:0;
  }
  window.pgScrollTo=function(target,offset,smooth){
    var id=String(target||'').replace(/^#/,'');
    if(!id)return;
    var el=document.getElementById(id)||document.querySelector('[name="'+id+'"]');
    if(!el)return;
    var reduce=window.matchMedia&&window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var off=(typeof offset==='number'?offset:headerOffset()+12);
    var y=el.getBoundingClientRect().top+(window.scrollY||0)-off;
    window.scrollTo({top:Math.max(0,y),behavior:(smooth===false||reduce)?'auto':'smooth'});
    /* Move focus so keyboard and screen-reader users follow the jump too;
       tabindex is removed again so the target doesn't become a tab stop. */
    if(!el.hasAttribute('tabindex')){
      el.setAttribute('tabindex','-1');
      el.addEventListener('blur',function h(){el.removeAttribute('tabindex');el.removeEventListener('blur',h);});
    }
    el.focus({preventScroll:true});
  };
  document.addEventListener('click',function(e){
    var a=e.target&&e.target.closest?e.target.closest('a[href^="#"]'):null;
    if(!a)return;
    var href=a.getAttribute('href')||'';
    if(href.length<2)return;
    var el=document.getElementById(href.slice(1));
    if(!el)return;
    e.preventDefault();
    window.pgScrollTo(href.slice(1));
    if(history.replaceState)history.replaceState(null,'',href);
  });
  /* A page LOADED at a #hash also needs the offset applied. */
  if(location.hash&&location.hash.length>1){
    setTimeout(function(){window.pgScrollTo(location.hash.slice(1),undefined,false);},60);
  }
})();`
}

/**
 * Every script the static site needs, in one string.
 *
 * Emitting them unconditionally is deliberate: each guards against
 * double-execution and no-ops when its markup is absent, and the whole
 * bundle is a few kilobytes — far cheaper than the per-feature conditional
 * emission it replaces, which is exactly the kind of place a surface drifts.
 */
export function pageRuntimeScript(): string {
  return [
    anchorScrollScript(),
    navDrawerScript(),
    scrollSpyScript(),
    marqueeDuplicate(),
    countUpScript(),
    lightboxScript(),
    beforeAfterScript(),
    pricingToggleScript(),
    carouselScript(),
    announcementScript(),
    formSubmitScript(),
    collectionSearchScript(),
    commentsScript(),
  ].join('\n')
}

// ── Static-site form submission ───────────────────────────────────────
/**
 * Wires a plain HTML `<form data-pg-form>` on the PUBLISHED STATIC SITE to
 * the built-in submissions store.
 *
 * The interactive surfaces submit through React (`PageForm`'s
 * `submitToStore`). The static export has no React, so without this a public
 * contact form rendered, accepted a message, and dropped it — the precise
 * failure the submissions store exists to fix, and the one place it matters
 * most, since public marketing pages are where public forms live.
 *
 * Progressive enhancement throughout: the markup is a real `<form>` with real
 * inputs and real `required` attributes, so browser validation works before
 * this runs. The script only replaces the navigation with a fetch so the
 * visitor stays on the page and sees the success state.
 */
export function formSubmitScript(): string {
  return `(function(){
  if(window.__pgForms)return;window.__pgForms=1;
  /* Mint a reCAPTCHA v3 token for one form, or null when no check applies.
     Resolves rather than rejects on every failure path — a blocked script, an
     offline visitor, a corporate proxy — because the SERVER decides what a
     missing token means, and for a form it means "store it and flag it". A
     script that could not load must never cost somebody their enquiry. */
  function token(f){
    var key=f.getAttribute('data-pg-captcha-key');
    var action=f.getAttribute('data-pg-captcha-action')||'form_submit';
    if(!key||!window.grecaptcha||!window.grecaptcha.execute)return Promise.resolve(null);
    return new Promise(function(resolve){
      var done=false;
      /* Slow is a worse failure than unprotected here: the visitor is looking
         at a spinner they did not ask for. */
      var timer=setTimeout(function(){if(!done){done=true;resolve(null);}},10000);
      try{
        window.grecaptcha.ready(function(){
          window.grecaptcha.execute(key,{action:action}).then(function(t){
            if(done)return;done=true;clearTimeout(timer);resolve(t||null);
          }).catch(function(){
            if(done)return;done=true;clearTimeout(timer);resolve(null);
          });
        });
      }catch(err){if(!done){done=true;clearTimeout(timer);resolve(null);}}
    });
  }
  document.addEventListener('submit',function(e){
    var f=e.target;
    if(!f||!f.hasAttribute||!f.hasAttribute('data-pg-form'))return;
    var url=f.getAttribute('data-pg-endpoint');
    if(!url)return;                       /* no API base configured — leave the form alone */
    e.preventDefault();
    var btn=f.querySelector('[type=submit]');
    if(btn){btn.disabled=true;btn.setAttribute('data-pg-busy','');}
    var values={};
    var els=f.querySelectorAll('input,select,textarea');
    for(var i=0;i<els.length;i++){
      var el=els[i];
      if(!el.name)continue;
      if(el.type==='checkbox'){values[el.name]=el.checked;continue;}
      if(el.type==='radio'){if(el.checked)values[el.name]=el.value;continue;}
      values[el.name]=el.value;
    }
    var started=parseInt(f.getAttribute('data-pg-started')||'0',10);
    /* Minted at SUBMIT, never when the page rendered: a v3 token expires after
       two minutes and most people read a form for longer than that. */
    token(f).then(function(tok){
      return fetch(url,{
        method:'POST',
        headers:{'Content-Type':'application/json'},
        body:JSON.stringify({
          block_id:f.getAttribute('data-pg-block')||'form',
          form_label:f.getAttribute('data-pg-label')||undefined,
          values:values,
          elapsed:started?(Date.now()-started)/1000:undefined,
          honeypot_name:f.getAttribute('data-pg-hp')||'_pg_hp',
          min_seconds:parseFloat(f.getAttribute('data-pg-min')||'2'),
          notify_email:f.getAttribute('data-pg-notify')||undefined,
          captcha_token:tok||undefined,
          captcha_mode:f.getAttribute('data-pg-captcha-mode')||undefined,
          captcha_on_low_score:f.getAttribute('data-pg-captcha-low')||undefined
        })
      });
    }).then(function(r){
      if(!r.ok)throw new Error('HTTP '+r.status);
      var redirect=f.getAttribute('data-pg-redirect');
      if(redirect){window.location.href=redirect;return;}
      var ok=f.parentNode.querySelector('[data-pg-form-success]');
      /* Both carry an inline display, which beats the UA's [hidden] rule,
         so the attribute alone neither showed the confirmation nor hid the
         form. Set the property too. */
      if(ok){ok.removeAttribute('hidden');ok.style.display='flex';
             f.setAttribute('hidden','');f.style.display='none';}
      else f.reset();
    }).catch(function(){
      /* Say so rather than silently thanking them for a message we dropped. */
      var err=f.parentNode.querySelector('[data-pg-form-error]');
      if(err)err.removeAttribute('hidden');
      if(btn){btn.disabled=false;btn.removeAttribute('data-pg-busy');}
    });
  });
  /* Stamp the render time on every form for the timing spam probe. */
  function stamp(){
    var fs=document.querySelectorAll('form[data-pg-form]:not([data-pg-started])');
    for(var i=0;i<fs.length;i++)fs[i].setAttribute('data-pg-started',String(Date.now()));
  }
  stamp();
  if(window.MutationObserver)new MutationObserver(stamp).observe(document.body,{childList:true,subtree:true});
})();`
}

/**
 * The `<script>` tag that loads reCAPTCHA v3 on a published static page, plus
 * the rule that hides Google's floating badge.
 *
 * Emitted only when the site actually HAS a key — unlike the rest of
 * `pageRuntimeScript`, this one pulls in a third-party script, and a page with
 * no form has no business making that request. The badge is hidden because it
 * floats over page content on a phone; Google's terms accept the written
 * notice instead, which every form renders beside its submit button.
 */
export function recaptchaScriptTag(siteKey: string): string {
  if (!siteKey) return ''
  const key = encodeURIComponent(siteKey)
  return `<style>.grecaptcha-badge{visibility:hidden;}</style>`
    + `<script src="https://www.google.com/recaptcha/api.js?render=${key}" async defer></script>`
}

// ── Filter bar ────────────────────────────────────────────────────────
//
// ONE implementation, two callers. The React surfaces import
// `initFilterBars` and call it from an effect; the static site gets the
// SAME function serialised by `filterBarScript()`. Writing it twice would
// mean a published catalogue filtering differently from the one in the
// designer, which is the whole class of bug the shared renderers exist to
// avoid — so it must stay closure-free and self-contained, because
// `Function.prototype.toString` is what ships it.

export function initFilterBars(root: Document | HTMLElement): void {
  const scope: Document | HTMLElement = root
  const bars = scope.querySelectorAll('[data-pg-filter]')
  for (let b = 0; b < bars.length; b++) {
    const bar = bars[b] as HTMLElement
    const targetId = bar.getAttribute('data-pg-filter') || ''
    // The target lives outside the bar, so search the whole document.
    const target = document.querySelector('[data-pg-block="' + targetId + '"]') as HTMLElement | null
    if (!target) continue

    const items = Array.prototype.slice.call(
      target.querySelectorAll('.pg-item'),
    ) as HTMLElement[]
    if (!items.length) continue

    // What to HIDE is not always the item itself. In an authored
    // `card_grid` the `.pg-item` is the grid child; in a data-bound grid
    // each copy is wrapped (per-block style, animation), so the grid child
    // is the wrapper and hiding the item left an empty cell behind — the
    // grid kept a hole where every filtered-out card had been. Hide the
    // ancestor that IS the grid child, and sort those too.
    const cellOf = (el: HTMLElement): HTMLElement => {
      let cur: HTMLElement = el
      while (cur.parentElement && cur.parentElement !== target) {
        cur = cur.parentElement
      }
      return cur.parentElement === target ? cur : el
    }
    const cells = items.map(cellOf)
    // Remember the authored order so "no sort" can restore it.
    for (let i = 0; i < cells.length; i++) {
      if (!cells[i].hasAttribute('data-pg-ord')) {
        cells[i].setAttribute('data-pg-ord', String(i))
      }
    }

    const state: { q: string; facets: Record<string, string>; sort: string } = {
      q: '', facets: {}, sort: '',
    }

    // Build one chip per distinct facet value, from the items themselves.
    // Cloning a rendered template rather than composing markup here keeps
    // the chips styled by the same renderer as everything else — and it is
    // what makes facets work on a PUBLISHED page, where no effect runs.
    const templates = bar.querySelectorAll('[data-pg-facet-chip-template]')
    for (let t = 0; t < templates.length; t++) {
      const tpl = templates[t] as HTMLElement
      const key = tpl.getAttribute('data-pg-facet-chip-template') || ''
      const host = bar.querySelector('[data-pg-facet-values="' + key + '"]') as HTMLElement | null
      if (!host || host.getAttribute('data-pg-filled') === '1') continue
      const proto = tpl.firstElementChild as HTMLElement | null
      if (!proto) continue
      const seen: string[] = []
      for (let i = 0; i < items.length; i++) {
        const v = items[i].getAttribute('data-pg-facet-' + key) || ''
        if (v && seen.indexOf(v) === -1) seen.push(v)
      }
      for (let i = 0; i < seen.length; i++) {
        const chip = proto.cloneNode(true) as HTMLElement
        chip.setAttribute('data-pg-facet-pick', key + '=' + seen[i])
        chip.setAttribute('aria-pressed', 'false')
        const count = chip.querySelector('[data-pg-count-for]')
        const label = document.createTextNode(seen[i])
        // The chip is "<label text><count badge>"; replace the empty label
        // node without disturbing the badge the counter writes into.
        chip.insertBefore(label, chip.firstChild)
        if (count) count.setAttribute('data-pg-count-for', key + '=' + seen[i])
        host.appendChild(chip)
      }
      host.setAttribute('data-pg-filled', '1')
    }

    const textOf = (el: HTMLElement) =>
      (el.getAttribute('data-pg-text') || el.textContent || '').toLowerCase()
    const facetOf = (el: HTMLElement, key: string) =>
      el.getAttribute('data-pg-facet-' + key) || ''

    const apply = () => {
      let shown = 0
      for (let i = 0; i < items.length; i++) {
        const el = items[i]
        const cell = cells[i]
        let ok = !state.q || textOf(el).indexOf(state.q) !== -1
        if (ok) {
          for (const key in state.facets) {
            const want = state.facets[key]
            if (want && facetOf(el, key) !== want) { ok = false; break }
          }
        }
        // `hidden` alone loses to an inline display, so set the property
        // too — and RESTORE the cell's own layout rather than clearing the
        // property, which would leave a flex card as a plain block.
        if (ok) {
          cell.removeAttribute('hidden')
          const was = cell.getAttribute('data-pg-disp')
          if (was === '') cell.style.removeProperty('display')
          else cell.style.display = was || 'flex'
          shown++
        } else {
          if (!cell.hasAttribute('data-pg-disp')) {
            cell.setAttribute('data-pg-disp', cell.style.display || '')
          }
          cell.setAttribute('hidden', '')
          cell.style.display = 'none'
        }
      }

      // Counts are derived from the items still passing every OTHER facet,
      // so a count never promises a combination that yields nothing.
      const counts = bar.querySelectorAll('[data-pg-count-for]')
      for (let c = 0; c < counts.length; c++) {
        const node = counts[c] as HTMLElement
        const spec = (node.getAttribute('data-pg-count-for') || '').split('=')
        const key = spec[0]
        const val = spec.slice(1).join('=')
        let n = 0
        for (let i = 0; i < items.length; i++) {
          const el = items[i]
          if (state.q && textOf(el).indexOf(state.q) === -1) continue
          let ok = true
          for (const k in state.facets) {
            if (k === key) continue
            if (state.facets[k] && facetOf(el, k) !== state.facets[k]) { ok = false; break }
          }
          if (ok && (!val || facetOf(el, key) === val)) n++
        }
        node.textContent = String(n)
      }

      if (state.sort) {
        const parts = state.sort.split(':')
        const by = parts[0]
        const dir = parts[1] === 'desc' ? -1 : 1
        const sorted = cells.slice().sort((x, y) => {
          if (by === 'none') {
            return (Number(x.getAttribute('data-pg-ord')) - Number(y.getAttribute('data-pg-ord')))
          }
          const sortAttr = (cell: HTMLElement) => {
            const own = cell.getAttribute('data-pg-sort-' + by)
            if (own !== null) return own
            const inner = cell.querySelector('[data-pg-sort-' + by + ']')
            return inner ? inner.getAttribute('data-pg-sort-' + by) || '' : ''
          }
          const ax = sortAttr(x)
          const ay = sortAttr(y)
          const nx = parseFloat(ax); const ny = parseFloat(ay)
          if (!isNaN(nx) && !isNaN(ny)) return (nx - ny) * dir
          return ax.localeCompare(ay) * dir
        })
        for (let i = 0; i < sorted.length; i++) target.appendChild(sorted[i])
      }

      const out = bar.querySelector('[data-pg-filter-count]')
      if (out) {
        const tpl = out.getAttribute('data-pg-filter-count') || '{n}'
        out.textContent = tpl.replace('{n}', String(shown))
      }
      const empty = document.querySelector('[data-pg-filter-empty="' + targetId + '"]') as HTMLElement | null
      if (empty) {
        if (shown === 0) { empty.removeAttribute('hidden'); empty.style.removeProperty('display') }
        else { empty.setAttribute('hidden', ''); empty.style.display = 'none' }
      }
    }

    bar.addEventListener('input', (e) => {
      const t = e.target as HTMLElement
      if (t && t.hasAttribute && t.hasAttribute('data-pg-filter-search')) {
        state.q = ((t as HTMLInputElement).value || '').toLowerCase().trim()
        apply()
      }
    })
    bar.addEventListener('change', (e) => {
      const t = e.target as HTMLElement
      if (!t || !t.hasAttribute) return
      if (t.hasAttribute('data-pg-filter-sort')) {
        state.sort = (t as HTMLSelectElement).value
        apply()
      }
    })
    bar.addEventListener('click', (e) => {
      let t = e.target as HTMLElement | null
      while (t && t !== bar && !(t.hasAttribute && t.hasAttribute('data-pg-facet-pick'))) {
        t = t.parentElement
      }
      if (!t || t === bar) return
      e.preventDefault()
      const spec = (t.getAttribute('data-pg-facet-pick') || '').split('=')
      const key = spec[0]
      const val = spec.slice(1).join('=')
      state.facets[key] = val
      const siblings = bar.querySelectorAll('[data-pg-facet-pick^="' + key + '="]')
      for (let i = 0; i < siblings.length; i++) {
        const el = siblings[i] as HTMLElement
        const on = (el.getAttribute('data-pg-facet-pick') || '') === key + '=' + val
        el.setAttribute('aria-pressed', on ? 'true' : 'false')
      }
      apply()
    })

    apply()
  }
}

/** The same function, for a page with no React on it. */
export function filterBarScript(): string {
  return `(function(){if(window.__pgFilter)return;window.__pgFilter=1;
  var init=${initFilterBars.toString()};
  function go(){init(document)}
  if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',go);else go();
})();`
}


// ── Static-site collection search ─────────────────────────────────────
/**
 * Drives a server-rendered `collection_search` block (`[data-pg-search]`,
 * markup from readerBlocks.tsx) on the static site: loads the index the
 * publisher wrote (`data-index`, e.g. /blog/search.json) on first use and
 * filters it as the visitor types — the SAME rule as `matchSearch`: every word
 * in the title/summary/terms, title hits first. `?q=` pre-fills the box.
 */
export function collectionSearchScript(): string {
  return `(function(){
  if(window.__pgSearch)return;window.__pgSearch=1;
  function esc(s){return String(s==null?'':s).replace(/[&<>"']/g,function(c){return {'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c];});}
  function match(items,q,limit){
    var words=q.toLowerCase().split(/\\s+/).filter(Boolean);if(!words.length)return [];
    var out=[];
    for(var i=0;i<items.length;i++){var it=items[i];var t=(it.t||'').toLowerCase();
      var hay=t+' '+(it.s||'').toLowerCase()+' '+(it.k||[]).join(' ').toLowerCase();
      var ok=true,score=0;for(var j=0;j<words.length;j++){if(hay.indexOf(words[j])<0){ok=false;break;}if(t.indexOf(words[j])>=0)score++;}
      if(ok)out.push({it:it,s:score});}
    out.sort(function(a,b){return b.s-a.s;});
    return out.slice(0,limit).map(function(x){return x.it;});
  }
  function wire(box){
    if(box.hasAttribute('data-pg-wired'))return;box.setAttribute('data-pg-wired','');
    var input=box.querySelector('[data-pg-search-input]'),list=box.querySelector('[data-pg-search-results]'),
        empty=box.querySelector('[data-pg-search-empty]'),url=box.getAttribute('data-index'),
        limit=parseInt(box.getAttribute('data-limit')||'12',10),items=null,loading=null;
    if(!input||!list||!url)return;
    function load(){if(items||loading)return loading;
      loading=fetch(url).then(function(r){return r.ok?r.json():[];}).then(function(d){items=Array.isArray(d)?d:[];return items;})
        .catch(function(){items=[];return items;});return loading;}
    function render(){var q=input.value.trim();
      Promise.resolve(q?load():null).then(function(){
        var res=q?match(items||[],q,limit):[];
        list.innerHTML=res.map(function(r){return '<li style="border-bottom:1px solid var(--pg-border,#e2e8f0);padding-bottom:10px">'+
          '<a href="'+esc(r.u)+'" style="font-weight:700;color:var(--pg-link,var(--pg-primary));text-decoration:none">'+esc(r.t)+'</a>'+
          (r.d?'<span style="margin-left:8px;font-size:.8rem;color:var(--pg-text-secondary,#64748b)">'+esc(r.d)+'</span>':'')+
          (r.s?'<p style="margin:4px 0 0;font-size:.88rem;color:var(--pg-text-secondary,#475569)">'+esc(r.s)+'</p>':'')+'</li>';}).join('');
        if(empty){if(q&&!res.length)empty.removeAttribute('hidden');else empty.setAttribute('hidden','');}
      });}
    input.addEventListener('focus',load);input.addEventListener('input',render);
    try{var q0=new URLSearchParams(location.search).get('q');if(q0){input.value=q0;render();}}catch(e){}
  }
  function scan(){var b=document.querySelectorAll('[data-pg-search]');for(var i=0;i<b.length;i++)wire(b[i]);}
  if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',scan);else scan();
})();`
}

// ── Static-site comments ──────────────────────────────────────────────
/**
 * Drives a server-rendered `comments` block (`[data-pg-comments]`): lists the
 * APPROVED comments from `data-endpoint` (live — approving a comment needs no
 * republish) and posts new ones there, with the honeypot and the render→submit
 * time the server's spam guard reads, plus a reCAPTCHA token when the site
 * has a key. Without an endpoint (no API base at publish) the form is hidden.
 */
export function commentsScript(): string {
  return `(function(){
  if(window.__pgComments)return;window.__pgComments=1;
  function esc(s){return String(s==null?'':s).replace(/[&<>"']/g,function(c){return {'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c];});}
  function token(){var el=document.querySelector('[data-pg-captcha-key]');var key=el&&el.getAttribute('data-pg-captcha-key');
    if(!key||!window.grecaptcha||!window.grecaptcha.execute)return Promise.resolve(null);
    return new Promise(function(res){var done=false;setTimeout(function(){if(!done){done=true;res(null);}},10000);
      try{window.grecaptcha.ready(function(){window.grecaptcha.execute(key,{action:'comment'}).then(function(t){if(!done){done=true;res(t);}},function(){if(!done){done=true;res(null);}});});}
      catch(e){if(!done){done=true;res(null);}}});}
  function wire(box){
    if(box.hasAttribute('data-pg-wired'))return;box.setAttribute('data-pg-wired','');
    var url=box.getAttribute('data-endpoint'),list=box.querySelector('[data-pg-comments-list]'),
        empty=box.querySelector('[data-pg-comments-empty]'),count=box.querySelector('[data-pg-comments-count]'),
        form=box.querySelector('[data-pg-comments-form]'),status=box.querySelector('[data-pg-comments-status]'),started=Date.now();
    if(!url){if(form)form.style.display='none';return;}
    function show(items){
      list.innerHTML=items.map(function(c){return '<li style="padding:12px 14px;border:1px solid var(--pg-border,#e2e8f0);border-radius:10px">'+
        '<strong>'+esc(c.name)+'</strong>'+(c.date?'<span style="margin-left:8px;font-size:.8rem;color:var(--pg-text-secondary,#64748b)">'+esc(c.date)+'</span>':'')+
        '<p style="margin:6px 0 0;white-space:pre-wrap">'+esc(c.body)+'</p></li>';}).join('');
      if(count)count.textContent=items.length?'('+items.length+')':'';
      if(empty){if(items.length)empty.setAttribute('hidden','');else empty.removeAttribute('hidden');}
    }
    function refresh(){return fetch(url).then(function(r){return r.ok?r.json():{comments:[]};}).then(function(d){show(d.comments||[]);}).catch(function(){});}
    refresh();
    if(!form)return;
    form.addEventListener('submit',function(e){
      e.preventDefault();var btn=form.querySelector('[type=submit]');if(btn)btn.disabled=true;
      function say(text,err){if(!status)return;status.textContent=text;status.style.color=err?'var(--pg-error,#dc2626)':'var(--pg-success,#16a34a)';status.removeAttribute('hidden');}
      token().then(function(tok){return fetch(url,{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({
        name:form.elements.name.value,email:form.elements.email.value,body:form.elements.body.value,
        hp:(form.elements._pg_hp||{}).value,elapsed:(Date.now()-started)/1000,captcha_token:tok||undefined})});})
      .then(function(r){return r.json().catch(function(){return {};}).then(function(d){
        /* Only the server's own sentence reaches the visitor; a network
           failure or an unexpected status gets the generic line below. */
        if(!r.ok){var e=new Error('');e.visible=(d&&typeof d.detail==='string')?d.detail:'';throw e;}return d;});})
      .then(function(d){form.reset();
        if(d.status==='approved'){refresh();say('Thanks — your comment is published.');}
        else say('Thanks — your comment will appear once it is approved.');})
      .catch(function(err){say((err&&err.visible)||'Could not post your comment — please try again.',true);})
      .then(function(){if(btn)btn.disabled=false;});
    });
  }
  function scan(){var b=document.querySelectorAll('[data-pg-comments]');for(var i=0;i<b.length;i++)wire(b[i]);}
  if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',scan);else scan();
})();`
}
